import React, { useState, useEffect } from "react";
import { Box, Grid, Text, Flex, Input, Select, Button, useColorModeValue, Spinner } from "@chakra-ui/react";
import Card from "components/card/Card";
import { useUser } from "contexts/UserContext";
import config from "config";
import toast from "react-hot-toast";

import Banner from "views/admin/profile/components/Banner";
import banner from "assets/img/auth/banner.png";

async function getDashboard() {
  const token = localStorage.getItem("token");
  const response = await fetch(`${config.BACKEND_API_URL}/auth/dashboard`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`
    }
  });

  return await response.json();
}

export default function Overview() {
  const textColor = useColorModeValue("#1B2559", "#FFFFFF");
  const labelColor = useColorModeValue("#4A5568", "#A3AED0");
  const inputBg = useColorModeValue("#FFFFFF", "#1B254B");
  const inputBorder = useColorModeValue("#E2E8F0", "#2A3A6B");

  const { user, setUser, region: globalRegion, updateRegion } = useUser();

  const [loading, setLoading] = useState(true);
  const [editName, setEditName] = useState("");
  const [editRegion, setEditRegion] = useState(globalRegion);
  const [savingConfig, setSavingConfig] = useState(false);

  const [dashboardData, setDashboardData] = useState(null);
  const [resetTime, setResetTime] = useState("...");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      const res = await getDashboard();
      const data = res.data;

      setDashboardData(data);

      setEditName(data.user.name);
      // Initialize editRegion from global state
      setEditRegion(globalRegion);

      setLoading(false);
    };

    fetchData();
  }, [globalRegion]);

  useEffect(() => {
    let timer;

    const updateTimer = () => {
      const now = new Date();

      const parts = new Intl.DateTimeFormat("en-IN", {
        timeZone: "Asia/Kolkata",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false
      }).formatToParts(now);

      const h = parseInt(parts.find(p => p.type === "hour").value);
      const m = parseInt(parts.find(p => p.type === "minute").value);
      const s = parseInt(parts.find(p => p.type === "second").value);

      const totalNow = h * 3600 + m * 60 + s;
      const totalMidnight = 24 * 3600;

      const diff = totalMidnight - totalNow;

      const hours = Math.floor(diff / 3600);
      const minutes = Math.floor((diff % 3600) / 60);
      const seconds = diff % 60;

      let timeStr = "";

      if (hours > 0) timeStr += `${hours}h `;
      if (minutes > 0) timeStr += `${minutes}m `;
      if (seconds > 0) timeStr += `${seconds}s`;

      setResetTime(timeStr.trim());

      const msToNextSecond = 1000 - (Date.now() % 1000);
      timer = setTimeout(updateTimer, msToNextSecond);
    };

    updateTimer();

    return () => clearTimeout(timer);
  }, []);

  const handleSaveSettings = () => {
    setSavingConfig(true);
    setTimeout(() => {
      setUser(prev => ({ ...prev, name: editName }));
      updateRegion(editRegion);
      setSavingConfig(false);
      toast.success("Settings saved!");
    }, 600);
  };

  if (loading || !dashboardData) {
    return (
      <Box pt={{ base: "130px", md: "80px", xl: "80px" }} w="100%" display="flex" justifyContent="center">
        <Spinner size="xl" color="brand.500" />
      </Box>
    );
  }

  const realKey = dashboardData.user.apiKey || "";

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <Grid templateColumns={{ base: "1fr", lg: "1fr" }} gap="20px" maxW="800px" mx="auto">

        <Banner
          banner={banner}
          avatar={user?.avatar}
          name={user?.name}
          plan={user?.plan}
          requests={dashboardData.user.used}
          remaining={dashboardData.user.limit - dashboardData.user.used}
          resetTime={resetTime}
        />

        <Card p="30px">
          <Text color={textColor} fontSize="xl" fontWeight="700" mb="20px">
            Account Settings
          </Text>

          <Flex direction="column" mb="20px">
            <Text mb="5px" fontSize="sm" color={labelColor}>Full Name</Text>
            <Input 
              value={editName} 
              onChange={(e) => setEditName(e.target.value)}
              color={textColor}
              bg={inputBg}
              border="1px solid"
              borderColor={inputBorder}
              _hover={{ borderColor: "brand.500" }}
              _focus={{ borderColor: "brand.500" }}
            />
          </Flex>

          <Flex direction="column" mb="20px">
            <Text mb="5px" fontSize="sm" color={labelColor}>API Key</Text>
            <Input
              value={realKey}
              isReadOnly
              color={textColor}
              fontWeight="600"
              letterSpacing="1px"
              bg={inputBg}
              border="1px solid"
              borderColor={inputBorder}
              _focus={{ borderColor: inputBorder }}
            />
          </Flex>

          <Grid templateColumns="1fr 1fr" gap="20px" mb="30px">
            <Flex direction="column">
              <Text mb="5px" fontSize="sm" color={labelColor}>Region</Text>
              <Select 
                value={editRegion} 
                onChange={(e) => setEditRegion(e.target.value)}
                color={textColor}
                bg={inputBg}
                border="1px solid"
                borderColor={inputBorder}
              >
                <option style={{ background: inputBg, color: textColor }} value="INDIA">India (₹)</option>
                <option style={{ background: inputBg, color: textColor }} value="USA">USA ($)</option>
                <option style={{ background: inputBg, color: textColor }} value="UK">UK (£)</option>
                <option style={{ background: inputBg, color: textColor }} value="UAE">UAE (د.إ)</option>
                <option style={{ background: inputBg, color: textColor }} value="EUROPE">Europe (€)</option>
              </Select>
            </Flex>

            <Flex direction="column">
              <Text mb="5px" fontSize="sm" color={labelColor}>Current Plan</Text>
              <Input 
                value={dashboardData.user.plan} 
                readOnly 
                variant="filled" 
                color={textColor}
                bg={inputBg}
                border="1px solid"
                borderColor={inputBorder}
                textTransform="capitalize"
              />
            </Flex>
          </Grid>

          <Flex justify="flex-end">
            <Button 
              onClick={handleSaveSettings} 
              isLoading={savingConfig}
              colorScheme="brand"
              fontWeight="600"
              fontSize="sm"
              px="30px"
            >
              Save Changes
            </Button>
          </Flex>

        </Card>
      </Grid>
    </Box>
  );
}
